import { createAsyncThunk } from "@reduxjs/toolkit";
import apiAxios, { apiAxiosUpload } from "helpers/apiAxios";
import { handleAsyncThunkError } from "redux/reduxHelpers";

// PIF check (Contract tab): Proposal Sales sent vs the PIF of a chosen CO#.
// Reducer-less on purpose — the tab owns its payload in local state, so these
// thunks never touch (or clobber) the jobBuilding slice.

// No contract yet: the check runs on the project's contract-less buildings.
const baseUrl = ({ projectId, jobBuildingId, contractId }) => (contractId
  ? `/crm/projects/${projectId}/job_buildings/${jobBuildingId}/contracts/${contractId}/pif_checks`
  : `/crm/projects/${projectId}/pif_checks`);

// Commercial → PIF Check list. params: { q, status, page, per_page, contract_id }
export const getPifCheckContracts = createAsyncThunk(
  "ContractPifCheck/getPifCheckContracts",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get("/crm/pif_checks", { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to load contracts"));
    }
  }
);

export const getPifComparison = createAsyncThunk(
  "ContractPifCheck/getPifComparison",
  async (ids, { rejectWithValue }) => {
    try {
      // A panel inside the project page: a 403 must empty the panel, not raise
      // the full-page 403 screen.
      const response = await apiAxios.get(baseUrl(ids), { skipAccessDeniedPage: true });
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to load the PIF check"));
    }
  }
);

export const savePifCheck = createAsyncThunk(
  "ContractPifCheck/savePifCheck",
  async ({ buildingId, data, ...ids }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.patch(`${baseUrl(ids)}/${buildingId}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to save the PIF check"));
    }
  }
);

export const loadPifCheckFromAi = createAsyncThunk(
  "ContractPifCheck/loadPifCheckFromAi",
  async ({ buildingId, ...ids }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${baseUrl(ids)}/${buildingId}/load_from_ai`);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to load the AI values"));
    }
  }
);

export const reviewPifCheck = createAsyncThunk(
  "ContractPifCheck/reviewPifCheck",
  async ({ buildingId, reopen = false, adaptationId, note, ...ids }, { rejectWithValue }) => {
    try {
      const action = reopen ? "unreview" : "review";
      const response = await apiAxios.post(`${baseUrl(ids)}/${buildingId}/${action}`, { adaptation_id: adaptationId, note });
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to update the review"));
    }
  }
);

export const uploadProposalDocument = createAsyncThunk(
  "ContractPifCheck/uploadProposalDocument",
  async ({ files, ...ids }, { rejectWithValue }) => {
    try {
      const response = await apiAxiosUpload.post(`${baseUrl(ids)}/upload_document`, { documents: files });
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to upload the proposal"));
    }
  }
);

export const removeProposalDocument = createAsyncThunk(
  "ContractPifCheck/removeProposalDocument",
  async ({ documentId, ...ids }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${baseUrl(ids)}/remove_document`, { document_id: documentId });
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to remove the proposal file"));
    }
  }
);

export const reextractProposal = createAsyncThunk(
  "ContractPifCheck/reextractProposal",
  async ({ blobId, ...ids }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${baseUrl(ids)}/reextract`, blobId ? { blob_id: blobId } : {});
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to re-run AI on the proposal"));
    }
  }
);
